import { OfficeIcon, PeopleIcon, PersonIcon } from '@econsult/econsult-ui/dist/assets';
import { LoaderRenderer } from 'components/loaderRenderer';
import { STRINGS } from 'config';
import _map from 'lodash/map';
import React from 'react';
import { Template } from 'store/reducer/templates';
import {
  SidebarEmptyGraphic,
  SidebarList,
  SidebarListHeading,
  SidebarListItem,
  SidebarListSubHeading,
} from './ModalStyles';
import { Message } from './serviceErrorStyles';
import { SidebarSkeleton } from './templatemangerSkeletons';

const {
  TEMPLATE_TYPE: { PERSONAL },
  DEFAULT_TEMPLATE_NAME,
  EMIS_WEB_TEMPLATE,
} = STRINGS.TEMPLATE;

function removeDefaultTemplate(arr: Template[]): Template[] {
  return arr.filter((item: Template) => ![DEFAULT_TEMPLATE_NAME, EMIS_WEB_TEMPLATE].includes(item.name));
}

interface TemplateListItemProps {
  template: Template;
  selectedTemplateId: string | null;
  onClickTemplate: (template: Template) => void;
  automationName: string;
  selectedTemplateRef: React.RefObject<HTMLLIElement>;
}

export const TemplateListItem: React.FC<TemplateListItemProps> = props => {
  const { template, selectedTemplateId, onClickTemplate, automationName, selectedTemplateRef } = props;
  const handleClickTemplate = (event: React.MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    onClickTemplate(template);
  };
  return (
    <SidebarListItem
      data-automation={automationName}
      isHighlighted={selectedTemplateId === template.id}
      ref={selectedTemplateId === template.id ? selectedTemplateRef : undefined}
      onClick={handleClickTemplate}
    >
      <SidebarListHeading data-automation="TemplateTitle" bold={template.isNewTemplate}>
        {template.name}
      </SidebarListHeading>
      <SidebarListSubHeading data-automation="TemplateVisibility">
        {template.type === PERSONAL ? (
          <>
            <PersonIcon />
            Personal
          </>
        ) : (
          <>
            <PeopleIcon />
            Shared
          </>
        )}
      </SidebarListSubHeading>
    </SidebarListItem>
  );
};

interface NoTemplatesMessageTextProps {
  isFilteringOnSearchTerm: boolean;
}

const getNoTemplatesMessageText = (
  isFilteringOnSearchTerm: NoTemplatesMessageTextProps['isFilteringOnSearchTerm'],
): string => {
  if (isFilteringOnSearchTerm) {
    return 'No templates found.';
  }
  return 'You do not have any templates.';
};

export const NoTemplatesMessage: React.FC<NoTemplatesMessageTextProps> = ({ isFilteringOnSearchTerm }) => (
  <SidebarEmptyGraphic data-automation="NoTemplatesMessage">
    <OfficeIcon />
    <Message>{getNoTemplatesMessageText(isFilteringOnSearchTerm)}</Message>
  </SidebarEmptyGraphic>
);

interface ListProps {
  clearTemplate: () => void;
  templatesLoadingState: string;
  isFilteringOnSearchTerm: boolean;
  isAddingNewTemplate: boolean;
  filteredTemplates: Template[];
  selectedTemplateId: string | null;
  onClickTemplate: (template: Template) => void;
  idPrefix: string;
  newTemplate: Template;
  selectedTemplateRef: React.RefObject<HTMLLIElement>;
}

export const List: React.FC<ListProps> = props => {
  const {
    clearTemplate,
    templatesLoadingState,
    isFilteringOnSearchTerm,
    isAddingNewTemplate,
    filteredTemplates,
    selectedTemplateId,
    onClickTemplate,
    idPrefix,
    newTemplate,
    selectedTemplateRef,
  } = props;

  const noTemplatesInCurrentList = !filteredTemplates.length && !isAddingNewTemplate;
  const finalFilteredTemplates = removeDefaultTemplate(filteredTemplates);
  const onClickList = (event: React.MouseEvent<HTMLUListElement>) => {
    if ((event.target as HTMLDivElement).id === `${idPrefix}List`) {
      clearTemplate();
    }
  };

  return (
    <SidebarList id={`${idPrefix}List`} onClick={onClickList}>
      <LoaderRenderer loaderComponent={<SidebarSkeleton />} loadingState={templatesLoadingState} delayLoader>
        {noTemplatesInCurrentList && <NoTemplatesMessage isFilteringOnSearchTerm={isFilteringOnSearchTerm} />}
        {isAddingNewTemplate && (
          <TemplateListItem
            template={{ ...newTemplate, name: 'New Template' }}
            selectedTemplateId={selectedTemplateId}
            onClickTemplate={onClickTemplate}
            automationName="NewTemplateItem"
            selectedTemplateRef={selectedTemplateRef}
          />
        )}
        {_map(finalFilteredTemplates, template => (
          <TemplateListItem
            template={template}
            selectedTemplateId={selectedTemplateId}
            onClickTemplate={onClickTemplate}
            key={template.id}
            automationName="TemplateItem"
            selectedTemplateRef={selectedTemplateRef}
          />
        ))}
      </LoaderRenderer>
    </SidebarList>
  );
};
